import { store } from '@/state/store';
import type { AuthUser } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Sdílené přihlášení s Janette.ai
// ═══════════════════════════════════════════════════════════════
//
// DŮLEŽITÉ (korekce specifikace, sekce 4):
// auth-worker.js autentizuje výhradně přes hlavičku `Authorization: Bearer <JWT>`,
// NE přes cookie. Token si Janette.ai drží v localStorage pod klíčem
// `janette_token`, který je per-origin — na subdoménu doc.janette.ai se
// automaticky NEPŘENESE. Sdílení session proto řešíme takto:
//
//   1) PRIMÁRNÍ (funguje bez zásahu do backendu):
//      Index stránka Janette.ai odkáže na DocEditor s tokenem ve fragmentu:
//         https://doc.janette.ai/#token=<JWT>
//      DocEditor token převezme, uloží do vlastního localStorage a fragment
//      z URL smaže (aby token nezůstal v historii/URL).
//
//   2) FALLBACK: token už v localStorage (uživatel se sem vrátil).
//
//   3) BUDOUCÍ (čistší UX, vyžaduje úpravu auth-workeru):
//      auth-worker by při loginu nastavil cookie `domain=.janette.ai`
//      a četl by ji jako alternativu k Bearer hlavičce. Do té doby platí (1).
//
// AUTH_URL musí ukazovat na nasazený auth-worker (stejný jako Janette.ai).

export const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://janette-auth.novyt.workers.dev';

const TOKEN_KEY = 'doceditor_token';

/** Vrátí uložený token nebo null. */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Převezme token z URL fragmentu (#token=...) a uklidí URL. */
function adoptTokenFromUrl(): void {
  const hash = window.location.hash;
  if (!hash.includes('token=')) return;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  if (token) {
    setToken(token);
    // Odstraň token z URL bez reloadu.
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

/**
 * Ověří session proti auth-workeru. Naplní store.user, nebo nechá null.
 * Vrací true pokud je uživatel přihlášený.
 */
export async function verifySession(): Promise<boolean> {
  adoptTokenFromUrl();
  const token = getToken();

  if (!token) {
    store.set({ user: null, authChecked: true });
    return false;
  }

  try {
    const res = await fetch(AUTH_URL + '/auth/me', {
      headers: { Authorization: 'Bearer ' + token },
    });
    if (!res.ok) {
      clearToken();
      store.set({ user: null, authChecked: true });
      return false;
    }
    const data = await res.json();
    const user: AuthUser = {
      id: data.id,
      email: data.email,
      name: data.name || '',
      tier: data.tier || 'free',
      picture: data.picture || undefined,
    };
    store.set({ user, authChecked: true });
    return true;
  } catch {
    // Síťová chyba — neodhlašuj natvrdo, jen označ jako nezkontrolováno online.
    store.set({ user: null, authChecked: true });
    return false;
  }
}

/** URL, kam poslat uživatele k přihlášení (Janette.ai login). */
export function loginRedirectUrl(): string {
  const app = import.meta.env.VITE_APP_LOGIN_URL || 'https://janette.ai';
  return app;
}
